import React, { useRef } from 'react';
import { Link } from 'react-router-dom';
import { ChevronLeft, ChevronRight, Star } from 'lucide-react';

const ContentRow = ({ title, items, type = 'movie', showRating = true }) => {
  const scrollRef = useRef(null);

  const scroll = (direction) => {
    if (scrollRef.current) {
      const scrollAmount = direction === 'left' ? -400 : 400;
      scrollRef.current.scrollBy({ left: scrollAmount, behavior: 'smooth' });
    }
  };

  return (
    <section className="py-6">
      <div className="max-w-[1800px] mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-xl font-semibold text-white">{title}</h2>
          <div className="flex gap-2">
            <button
              onClick={() => scroll('left')}
              className="p-2 bg-white/10 rounded-full hover:bg-white/20 transition-colors"
            >
              <ChevronLeft className="w-4 h-4 text-white" />
            </button>
            <button
              onClick={() => scroll('right')}
              className="p-2 bg-white/10 rounded-full hover:bg-white/20 transition-colors"
            >
              <ChevronRight className="w-4 h-4 text-white" />
            </button>
          </div>
        </div>

        {/* Content Grid */}
        <div
          ref={scrollRef}
          className="flex gap-4 overflow-x-auto pb-4 scrollbar-hide scroll-smooth"
        >
          {items.map((item) => (
            <Link
              key={item.stream_id || item.series_id || item.id}
              to={`/${type}/${item.stream_id || item.series_id || item.id}`}
              className="flex-shrink-0 group"
            >
              <div className="relative w-40 sm:w-44 rounded-lg overflow-hidden">
                {/* Poster */}
                <div className="aspect-[2/3] bg-gray-800">
                  <img
                    src={item.stream_icon || item.cover || item.poster}
                    alt={item.name || item.title}
                    className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-110"
                    loading="lazy"
                  />
                </div>

                {/* Hover Overlay */}
                <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/30 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                  <div className="absolute bottom-0 left-0 right-0 p-3">
                    <h3 className="text-white text-sm font-medium line-clamp-2">
                      {item.name || item.title}
                    </h3>
                    {showRating && (item.rating || item.rating_5based) && (
                      <div className="flex items-center gap-1 mt-1">
                        <Star className="w-3 h-3 text-yellow-400 fill-yellow-400" />
                        <span className="text-xs text-gray-300">
                          {item.rating || (item.rating_5based * 2).toFixed(1)}/10
                        </span>
                      </div>
                    )}
                    {item.year && (
                      <span className="text-xs text-gray-400 mt-1 block">{item.year}</span>
                    )}
                  </div>
                </div>
              </div>

              {/* Title below poster (visible on mobile) */}
              <div className="mt-2 sm:hidden">
                <h3 className="text-white text-xs font-medium line-clamp-1">
                  {item.name || item.title}
                </h3>
              </div>
            </Link>
          ))}
        </div>
      </div>
    </section>
  );
};

export default ContentRow;
